import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, MapPin, Baby, Award, LifeBuoy } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { API } from "../lib/utils";

const HERO_IMG =
  "https://images.unsplash.com/photo-1651614158095-b98b6c1da74b?crop=entropy&cs=srgb&fm=jpg&w=900&q=80";
const BAND_IMG =
  "https://images.unsplash.com/photo-1639477651629-3afd5d79654d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

const ModuleCard = ({ p, lang, bookLabel }) => (
  <article
    data-testid={`program-${p.slug}`}
    className="bg-white border border-border p-8 flex flex-col hover:-translate-y-1 transition-transform duration-300"
  >
    <span className="font-heading uppercase tracking-widest text-xs font-bold text-burnt">
      {lang === "fr" ? p.levels_fr : p.levels_en}
    </span>
    <h3 className="mt-3 hd-italic text-3xl text-forest leading-tight">{lang === "fr" ? p.title_fr : p.title_en}</h3>
    <span className="mt-3 inline-block self-start bg-bone text-forest font-heading uppercase tracking-widest text-[0.7rem] font-bold px-3 py-1">
      {lang === "fr" ? p.age_fr : p.age_en}
    </span>
    <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{lang === "fr" ? p.desc_fr : p.desc_en}</p>
    <Link
      to={`/reservation?program=${p.slug}&sport=swim`}
      data-testid={`program-${p.slug}-book`}
      className="mt-6 inline-flex items-center gap-2 font-heading uppercase tracking-widest text-xs font-bold text-forest hover:text-burnt transition-colors"
    >
      {bookLabel} <ArrowRight size={16} />
    </Link>
  </article>
);

const KidsSwimPage = () => {
  const { t, lang } = useLang();
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    axios.get(`${API}/programs`).then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  const kidsModules = programs.filter((p) => p.category === "kids");
  const adultModules = programs.filter((p) => p.category === "adults");
  const surviveSkills = ["s1", "s2", "s3"];
  const why = [
    { key: "why1", Icon: ShieldCheck },
    { key: "why2", Icon: Sparkles },
    { key: "why3", Icon: MapPin },
  ];

  return (
    <div data-testid="page-kids" className="bg-white">
      {/* ===== HERO ===== */}
      <section className="container-flow section-pad">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="text-overline inline-flex items-center gap-2" data-testid="kids-overline">
              <Baby size={18} className="text-burnt" /> {t("kidspage.overline")}
            </p>
            <h1 className="mt-4 hd-italic text-5xl md:text-7xl text-forest leading-[0.95]">
              {t("kidspage.title")}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
              {t("kidspage.lead")}
            </p>
            <div
              className="mt-8 inline-flex items-center gap-2 bg-bone border border-border px-4 py-2"
              data-testid="kids-badge"
            >
              <ShieldCheck size={16} className="text-burnt" />
              <span className="text-sm text-forest font-medium">{t("kidspage.badge")}</span>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/reservation" data-testid="kids-page-cta" className="btn-primary">
                {t("kidspage.cta")} <ArrowRight size={18} />
              </Link>
              <Link to="/notre-histoire" data-testid="kids-page-cta-secondary" className="btn-secondary">
                {t("kidspage.cta_secondary")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden border-4 border-white shadow-xl">
              <img src={HERO_IMG} alt="Cours de natation pour enfants" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/25 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== KIDS PROGRAMS ===== */}
      <section className="bg-bone section-pad border-t border-border">
        <div className="container-flow">
          <h2 className="hd-italic text-4xl md:text-5xl text-forest mb-12">{t("kidspage.kids_groups_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {kidsModules.map((p) => (
              <ModuleCard key={p.id} p={p} lang={lang} bookLabel={t("kidspage.book_module")} />
            ))}
          </div>

          {/* Bronze Star highlight */}
          <div
            data-testid="kids-bronze"
            className="mt-8 bg-forest text-white p-10 flex flex-col md:flex-row md:items-center justify-between gap-6"
          >
            <div className="flex items-start gap-5">
              <Award size={40} className="text-energy shrink-0" />
              <div>
                <p className="font-heading uppercase tracking-widest text-xs font-bold text-energy">
                  {t("kidspage.bronze_label")}
                </p>
                <h3 className="mt-1 hd-italic text-3xl">{t("kidspage.bronze_title")}</h3>
                <p className="mt-2 text-white/85 max-w-2xl leading-relaxed">{t("kidspage.bronze_desc")}</p>
              </div>
            </div>
            <Link
              to="/reservation?program=bronze&sport=swim"
              data-testid="kids-bronze-cta"
              className="btn-ghost !border-white !bg-transparent hover:!bg-white hover:!text-forest shrink-0"
            >
              {t("kidspage.cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== ADULTS & FITNESS ===== */}
      <section className="bg-white section-pad">
        <div className="container-flow">
          <h2 className="hd-italic text-4xl md:text-5xl text-forest mb-12">{t("kidspage.adults_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {adultModules.map((p) => (
              <ModuleCard key={p.id} p={p} lang={lang} bookLabel={t("kidspage.book_module")} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== SWIM TO SURVIVE ===== */}
      <section className="bg-forest text-white section-pad" data-testid="kids-survive">
        <div className="container-flow">
          <p className="font-heading uppercase tracking-widest text-xs font-bold text-energy inline-flex items-center gap-2">
            <LifeBuoy size={18} /> {t("kidspage.survive_label")}
          </p>
          <h2 className="mt-3 hd-italic text-4xl md:text-5xl leading-tight">{t("kidspage.survive_title")}</h2>
          <p className="mt-6 text-white/85 text-lg leading-relaxed max-w-3xl">{t("kidspage.survive_lead")}</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {surviveSkills.map((k, i) => (
              <div
                key={k}
                data-testid={`survive-${k}`}
                className="border border-white/20 bg-white/5 p-8"
              >
                <span className="hd-italic text-5xl text-energy">{`0${i + 1}`}</span>
                <h3 className="mt-4 font-heading uppercase tracking-wide text-lg font-bold leading-tight">
                  {t(`kidspage.${k}_skill`)}
                </h3>
                <p className="mt-3 text-white/80 leading-relaxed">{t(`kidspage.${k}_goal`)}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/reservation?program=survive&sport=swim"
              data-testid="kids-survive-cta"
              className="btn-ghost !border-white !bg-transparent hover:!bg-white hover:!text-forest"
            >
              {t("kidspage.cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== BAND IMAGE ===== */}
      <section className="relative h-[320px] md:h-[420px] overflow-hidden">
        <img src={BAND_IMG} alt="Enfant en cours de natation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/55" />
        <div className="relative container-flow h-full flex items-center">
          <p className="hd-italic text-3xl md:text-5xl text-white max-w-2xl leading-tight">
            {t("kidspage.lead")}
          </p>
        </div>
      </section>

      {/* ===== WHY ===== */}
      <section className="bg-white section-pad">
        <div className="container-flow">
          <h2 className="hd-italic text-4xl md:text-5xl text-forest mb-12">{t("kidspage.why_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {why.map(({ key, Icon }) => (
              <div key={key} data-testid={`kids-why-${key}`} className="border-t-2 border-burnt pt-6">
                <Icon size={32} className="text-burnt mb-4" />
                <h3 className="hd-italic text-2xl text-forest">{t(`kidspage.${key}_title`)}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{t(`kidspage.${key}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA STRIP ===== */}
      <section className="bg-burnt text-white section-pad">
        <div className="container-flow flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="hd-italic text-4xl md:text-6xl leading-tight">{t("kidspage.title")}</h2>
          <Link
            to="/reservation"
            data-testid="kids-cta-strip"
            className="btn-ghost !border-white !bg-transparent hover:!bg-white hover:!text-burnt"
          >
            {t("kidspage.cta")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default KidsSwimPage;
